import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchCatchBoardDuplicateVenueWarnings } from 'src/utils/catchBoardApi'
import { normalizeCatchBoardVenueIds } from 'src/utils/catchBoardDuplicateVenues'

/**
 * Live duplicate-venue warnings for create/edit deal forms.
 * Refetches when linked venue ids change. Failures are soft (empty warnings).
 *
 * @param {{
 *   venueIds?: Array<string|null|undefined>,
 *   excludeDealId?: string|null,
 *   enabled?: boolean,
 * }} [options]
 */
export function useCatchBoardDuplicateVenueWarnings({
  venueIds = [],
  excludeDealId = null,
  enabled = true,
} = {}) {
  const normalizedVenueIds = useMemo(
    () => normalizeCatchBoardVenueIds(venueIds),
    [venueIds],
  )
  const venueKey = normalizedVenueIds.join(',')
  const excludeKey = excludeDealId ? String(excludeDealId) : ''

  const [warnings, setWarnings] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!enabled || !venueKey) {
      setWarnings([])
      setLoading(false)
      setError(null)
      return undefined
    }

    const requestId = ++requestIdRef.current
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const next = await fetchCatchBoardDuplicateVenueWarnings({
          venueIds: venueKey.split(','),
          excludeDealId: excludeKey || null,
        })
        if (cancelled || requestId !== requestIdRef.current) {
          return
        }
        setWarnings(next)
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) {
          return
        }
        setWarnings([])
        setError(err.message || 'Failed to check venue duplicates')
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [enabled, venueKey, excludeKey])

  return {
    warnings,
    loading,
    error,
  }
}
